import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList } from '../api/dataverseClient';
import { Project, TaskStatus } from '../types';
import { listProjects } from './projectService';
import { ResourcePoolMember } from './resourcePoolService';

// ── Workload / conflict analysis for the Global Resource Pool ──────────
//
// Allocation is derived from real task data: assignee × estimated hours ×
// [start, due] date range, spread evenly across the working days (Mon–Fri)
// of each task, across every project linked to the pool. Capacity per
// person is their default hours/day × working days in the bucket. When a
// person's allocated hours exceed capacity in a bucket they are
// over-allocated; when that over-allocation also spans two or more
// projects it is flagged as a cross-project conflict.

const DAY_MS = 86400000;

export interface WorkloadTask {
  dagrun_pm_taskid: string;
  dagrun_pm_name: string;
  dagrun_pm_assigneeid: string | null;
  dagrun_pm_estimatedhours: number | null;
  dagrun_pm_startdate: string | null;
  dagrun_pm_duedate: string | null;
  _dagrun_pm_projectid_value: string | null;
}

const WORKLOAD_TASK_FIELDS = [
  'dagrun_pm_taskid', 'dagrun_pm_name', 'dagrun_pm_assigneeid',
  'dagrun_pm_estimatedhours', 'dagrun_pm_startdate', 'dagrun_pm_duedate',
  '_dagrun_pm_projectid_value',
];

/** Projects linked to the global resource pool. */
export async function getPoolLinkedProjects(pca: IPublicClientApplication): Promise<Project[]> {
  const all = await listProjects(pca);
  return all.filter(p => p.dagrun_pm_useglobalresourcepool === true);
}

/**
 * Tasks that contribute to workload across the given pool-linked projects:
 * an assignee, a positive estimate, both a start and due date, and not
 * Done/Cancelled (completed / dead work doesn't consume forward capacity).
 * Chunked OR filter on project id keeps the URL length bounded.
 */
export async function getWorkloadTasks(
  pca: IPublicClientApplication,
  projectIds: string[],
): Promise<WorkloadTask[]> {
  if (projectIds.length === 0) return [];
  const out: WorkloadTask[] = [];
  const CHUNK = 15;
  for (let i = 0; i < projectIds.length; i += CHUNK) {
    const chunk = projectIds.slice(i, i + CHUNK);
    const projClause = chunk.map(id => `_dagrun_pm_projectid_value eq ${id}`).join(' or ');
    const filter =
      `(${projClause}) and dagrun_pm_assigneeid ne null` +
      ` and dagrun_pm_status ne ${TaskStatus.Done} and dagrun_pm_status ne ${TaskStatus.Cancelled}`;
    try {
      const rows = await dvList<WorkloadTask>(pca, 'dagrun_pm_tasks', {
        select: WORKLOAD_TASK_FIELDS, filter, top: 2000,
      });
      out.push(...rows);
    } catch { /* one bad chunk shouldn't sink the whole view */ }
  }
  return out;
}

// ── Pure computation ───────────────────────────────────────────────────

export interface WorkloadFilters {
  memberUserId?: string | null;   // restrict to one resource
  projectId?: string | null;      // restrict allocation to one project
  rangeStart: Date;
  rangeEnd: Date;
}

export interface BucketCell {
  startMs: number;                // Monday of the week bucket
  hours: number;                  // allocated hours this bucket
  capacity: number;               // hours/day × working days in bucket
  utilization: number;            // hours / capacity (0..n)
  projectIds: string[];           // distinct pool projects contributing
  overallocated: boolean;         // hours > capacity
  conflict: boolean;              // overallocated AND ≥2 projects
}

export interface MemberWorkload {
  userId: string;
  name: string;
  hoursPerDay: number;
  buckets: BucketCell[];
  totalHours: number;
  hasConflict: boolean;
  hasOverallocation: boolean;
}

/**
 * Per-resource capacity: which days are working days, and hours per working day.
 * Injected into computeWorkload so BOTH the Portfolio view (pool default hours,
 * Mon–Fri) and the project Resources tab (Block A work-calendar fallback chain)
 * share ONE allocation calculation instead of duplicating the math.
 */
export interface WorkCapacity {
  isWorkingDay: (dayMs: number) => boolean;
  hoursPerDay: number;
}

/** Mon–Fri predicate — the default working-day rule used by the Portfolio view. */
export function monFriWorkingDay(ms: number): boolean {
  const d = new Date(ms).getUTCDay();
  return d !== 0 && d !== 6; // exclude Sun(0) / Sat(6)
}

/** Midnight-UTC of the Monday on/before the given date. */
function weekStartMs(ms: number): number {
  const d = new Date(ms);
  const day = d.getUTCDay();               // 0=Sun..6=Sat
  const backToMon = (day + 6) % 7;         // Mon→0, Sun→6
  const monday = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - backToMon * DAY_MS;
  return monday;
}

function dayFloorMs(iso: string): number {
  const d = new Date(iso);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Count working days in [startMs, endMs] inclusive under a given predicate. */
function workingDaysBetween(startMs: number, endMs: number, isWD: (ms: number) => boolean): number {
  if (endMs < startMs) return 0;
  let count = 0;
  for (let t = startMs; t <= endMs; t += DAY_MS) if (isWD(t)) count++;
  return count;
}

/** Ordered list of Monday bucket-start timestamps covering [start, end]. */
export function buildWeekBuckets(rangeStart: Date, rangeEnd: Date): number[] {
  const first = weekStartMs(rangeStart.getTime());
  const lastWeek = weekStartMs(rangeEnd.getTime());
  const weeks: number[] = [];
  for (let t = first; t <= lastWeek; t += 7 * DAY_MS) weeks.push(t);
  return weeks;
}

/**
 * Compute weekly workload per pool member from the task set. Pure — the
 * caller supplies the already-fetched members/tasks so this is trivially
 * testable and re-runs instantly on filter changes.
 */
export function computeWorkload(
  members: ResourcePoolMember[],
  tasks: WorkloadTask[],
  filters: WorkloadFilters,
  capacityFor: (userId: string) => WorkCapacity,
): MemberWorkload[] {
  const rangeStartMs = Date.UTC(filters.rangeStart.getUTCFullYear(), filters.rangeStart.getUTCMonth(), filters.rangeStart.getUTCDate());
  const rangeEndMs = Date.UTC(filters.rangeEnd.getUTCFullYear(), filters.rangeEnd.getUTCMonth(), filters.rangeEnd.getUTCDate());
  const weeks = buildWeekBuckets(filters.rangeStart, filters.rangeEnd);

  const visibleMembers = members.filter(m =>
    !filters.memberUserId || m.dagrun_pm_userid === filters.memberUserId);

  // Per member: bucketStartMs → { hours, projectIds:Set }
  const perMember = new Map<string, Map<number, { hours: number; projects: Set<string> }>>();
  const ensure = (uid: string, wk: number) => {
    let m = perMember.get(uid);
    if (!m) { m = new Map(); perMember.set(uid, m); }
    let c = m.get(wk);
    if (!c) { c = { hours: 0, projects: new Set() }; m.set(wk, c); }
    return c;
  };

  const memberIds = new Set(visibleMembers.map(m => m.dagrun_pm_userid ?? ''));

  for (const t of tasks) {
    const uid = t.dagrun_pm_assigneeid;
    if (!uid || !memberIds.has(uid)) continue;
    if (filters.projectId && t._dagrun_pm_projectid_value !== filters.projectId) continue;
    const est = t.dagrun_pm_estimatedhours ?? 0;
    if (est <= 0 || !t.dagrun_pm_startdate || !t.dagrun_pm_duedate) continue;

    // Spread the estimate across the ASSIGNEE's own working days (their calendar).
    const isWD = capacityFor(uid).isWorkingDay;
    const spanStart = dayFloorMs(t.dagrun_pm_startdate);
    const spanEnd = dayFloorMs(t.dagrun_pm_duedate);
    if (spanEnd < spanStart) continue;
    const spanWorkingDays = workingDaysBetween(spanStart, spanEnd, isWD);
    if (spanWorkingDays === 0) continue;
    const hoursPerWorkingDay = est / spanWorkingDays;
    const pid = t._dagrun_pm_projectid_value ?? '';

    // Spread across working days that also fall in the visible range.
    const from = Math.max(spanStart, rangeStartMs);
    const to = Math.min(spanEnd, rangeEndMs);
    for (let d = from; d <= to; d += DAY_MS) {
      if (!isWD(d)) continue;
      const wk = weekStartMs(d);
      const cell = ensure(uid, wk);
      cell.hours += hoursPerWorkingDay;
      if (pid) cell.projects.add(pid);
    }
  }

  return visibleMembers.map<MemberWorkload>(m => {
    const uid = m.dagrun_pm_userid ?? '';
    const cap = capacityFor(uid);
    const hpd = cap.hoursPerDay;
    const bucketMap = perMember.get(uid);
    let totalHours = 0, hasConflict = false, hasOver = false;
    const buckets = weeks.map<BucketCell>(wk => {
      const cell = bucketMap?.get(wk);
      const hours = cell?.hours ?? 0;
      // Capacity uses only the resource's working days of the week that fall in range.
      const wkEnd = Math.min(wk + 6 * DAY_MS, rangeEndMs);
      const wkStart = Math.max(wk, rangeStartMs);
      const capacity = workingDaysBetween(wkStart, wkEnd, cap.isWorkingDay) * hpd;
      const utilization = capacity > 0 ? hours / capacity : (hours > 0 ? Infinity : 0);
      const projectIds = cell ? Array.from(cell.projects) : [];
      const overallocated = capacity > 0 && hours > capacity + 1e-6;
      const conflict = overallocated && projectIds.length >= 2;
      totalHours += hours;
      if (overallocated) hasOver = true;
      if (conflict) hasConflict = true;
      return { startMs: wk, hours, capacity, utilization, projectIds, overallocated, conflict };
    });
    return {
      userId: uid, name: m.dagrun_pm_username ?? m.dagrun_pm_name ?? 'Unknown',
      hoursPerDay: hpd, buckets, totalHours, hasConflict, hasOverallocation: hasOver,
    };
  });
}
